/**
 * Tilauslistan filtterit
 */

import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Calendar, Search } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { OrderFilters } from "@/types/orders";

interface OrdersFiltersProps {
  filters: OrderFilters;
  onFiltersChange: (filters: OrderFilters) => void;
}

export function OrdersFilters({ filters, onFiltersChange }: OrdersFiltersProps) {
  const updateFilter = useCallback(
    <K extends keyof OrderFilters>(key: K, value: OrderFilters[K]) => {
      onFiltersChange({ ...filters, [key]: value });
    },
    [filters, onFiltersChange]
  );

  const clearDates = useCallback(() => {
    onFiltersChange({ ...filters, dateFrom: undefined, dateTo: undefined });
  }, [filters, onFiltersChange]);

  return (
    <div className="flex flex-col gap-4 mb-6">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Hae tilausnumerolla, asiakkaalla, hautausmaalla tai kumppanilla..."
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Order Type */}
        <Select
          value={filters.orderType}
          onValueChange={(v) => updateFilter("orderType", v)}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Kaikki tyypit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Kaikki tyypit</SelectItem>
            <SelectItem value="new_work">🔨 Uusi työ</SelectItem>
            <SelectItem value="pickup_work">🔄 Noudettava</SelectItem>
            <SelectItem value="field_work">🔧 Kenttätyö</SelectItem>
          </SelectContent>
        </Select>

        {/* Status */}
        <Select
          value={filters.status}
          onValueChange={(v) => updateFilter("status", v)}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Kaikki tilat" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Kaikki tilat</SelectItem>
            <SelectItem value="draft">Luonnos</SelectItem>
            <SelectItem value="confirmed">Vahvistettu</SelectItem>
            <SelectItem value="ready_for_installation">Valmis asennukseen</SelectItem>
            <SelectItem value="installed">Asennettu</SelectItem>
            <SelectItem value="completed">Valmis</SelectItem>
            <SelectItem value="cancelled">Peruutettu</SelectItem>
          </SelectContent>
        </Select>

        {/* Approval */}
        <Select
          value={filters.approval}
          onValueChange={(v) => updateFilter("approval", v)}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Seurakunnan hyväksyntä" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Kaikki hyväksynnät</SelectItem>
            <SelectItem value="requires_approval">Vaatii hyväksyntää</SelectItem>
            <SelectItem value="pending">⏳ Odottaa</SelectItem>
            <SelectItem value="approved">✅ Hyväksytty</SelectItem>
            <SelectItem value="rejected">❌ Hylätty</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Date Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !filters.dateFrom && "text-muted-foreground"
              )}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {filters.dateFrom ? format(filters.dateFrom, "dd.MM.yyyy") : "Alkaen"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={filters.dateFrom}
              onSelect={(date) => updateFilter("dateFrom", date)}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !filters.dateTo && "text-muted-foreground"
              )}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {filters.dateTo ? format(filters.dateTo, "dd.MM.yyyy") : "Päättyen"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={filters.dateTo}
              onSelect={(date) => updateFilter("dateTo", date)}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>

        {(filters.dateFrom || filters.dateTo) && (
          <Button variant="ghost" onClick={clearDates}>
            Tyhjennä päivämäärät
          </Button>
        )}
      </div>
    </div>
  );
}
